import { type CoreEventBusEvents } from '@databases/core-event-bus';
import { type ViewIdentityChanges } from '@databases/database-state';
import { container } from '@databases/service-container';
import { captureException } from '@sentry/core';

import { type CurrentViewIdentity } from './get-current-view-layout-type.ts';
import { subscribeActiveViewIdentityChanges } from './subscribe-active-view-identity-changes.ts';
import {
  type Recyclable,
  type RecyclableDisposeContext,
  type RecyclableViewTrigger,
} from './types.ts';

/**
 * Registers modules in dependency order. Every module recycles when bridge
 * database identity changes; modules can opt into view ID or layout type changes.
 *
 * @param modules - Dependencies before dependents. Setup is forward; disposal is reverse.
 * @param initialContentId - Content ID for the initial setup.
 * @returns A cleanup function that disposes all modules and unsubscribes from the event bus.
 *
 * Modules with `skipInitialSetup === true` skip registration setup only;
 * identity-driven recycling still invokes `setup` for every module.
 *
 * ---
 *
 * ## Why use this instead of `useEffect` in React components
 *
 * **1. Centralized, ordered execution.**
 * All identity-change side effects fire from this single call site in bootstrap, in a guaranteed
 * sequence: every `dispose` runs before any `setup`. `useEffect`s scattered across the component
 * tree are scheduled independently by React — there are no ordering guarantees between them.
 *
 * **2. Non-UI code does not belong in the React tree.**
 * Closing a side panel or invalidating a cache has nothing to do with rendering. Placing it in a
 * component couples it to that component's mount/unmount lifecycle. If the component ever moves,
 * gets lazy-loaded, or unmounts early, the side effect silently stops firing.
 *
 * **3. Performance — no extra render cycles.**
 * A `useEffect` on `contentId` re-runs after every commit where `contentId` changed. Five such
 * effects at different tree levels means five separate post-commit scheduler passes. Recyclables
 * run synchronously in bootstrap — zero render cycles triggered, zero scheduler overhead.
 *
 * **4. Easier to test in isolation.**
 * A `Recyclable` is a plain object with `setup` and `dispose`. Tests call those methods directly
 * with no React renderer, no `act()`, and no component tree. Testing `useEffect` always requires
 * mounting a component.
 *
 * **5. Explicit lifecycle — no accidental double-fire.**
 * Each `useEffect` must defensively track "did I already run for this id?" with a `useRef`.
 * Recyclables receive a `dispose` call before the next `setup`, so the lifecycle is explicit by
 * design rather than guarded by refs.
 */
export function registerRecyclableModules(
  modules: Recyclable[],
  initialContentId: string,
  readViewIdentity: (contentId: string) => CurrentViewIdentity,
): VoidFunction {
  let isFirstSetupPass = true;
  let currentContentId = initialContentId;

  function setupModules(modulesToSetup: readonly Recyclable[], contentId: string) {
    for (const module of modulesToSetup) {
      if (isFirstSetupPass && module.skipInitialSetup === true) {
        continue;
      }

      module.setup(contentId);
    }

    isFirstSetupPass = false;
  }

  function disposeModules(
    modulesToDispose: readonly Recyclable[],
    context?: RecyclableDisposeContext,
  ) {
    // Dependents release provider-owned resources before their providers are disposed.
    for (let index = modulesToDispose.length - 1; index >= 0; index--) {
      try {
        modulesToDispose[index]?.dispose(context);
      } catch (error) {
        captureException(error);
      }
    }
  }

  function recycleModules(
    modulesToRecycle: readonly Recyclable[],
    context: RecyclableDisposeContext,
  ) {
    if (modulesToRecycle.length === 0) {
      return;
    }

    disposeModules(modulesToRecycle, context);
    setupModules(modulesToRecycle, currentContentId);
  }

  setupModules(modules, initialContentId);

  const eventBus = container.resolve('EventBus');

  const onViewIdentityChanged = (changes: ViewIdentityChanges) => {
    const triggers: RecyclableViewTrigger[] = [];

    if (changes.viewIdChanged) {
      triggers.push('view-id-changed');
    }
    if (changes.layoutTypeChanged) {
      triggers.push('layout-type-changed');
    }

    const affectedModules = modules.filter((module) =>
      module.recycleOn?.some((trigger) => triggers.includes(trigger)),
    );

    recycleModules(affectedModules, {
      reason: 'view-identity-changed',
      triggers,
    });
  };

  let unsubscribeViewIdentity: VoidFunction | undefined =
    subscribeActiveViewIdentityChanges(
      currentContentId,
      onViewIdentityChanged,
      readViewIdentity,
    );

  const onBridgeIdentityChanged = (
    data: CoreEventBusEvents['bridgeDatabaseIdentityChanged'],
  ) => {
    const stopPrevious = unsubscribeViewIdentity;
    unsubscribeViewIdentity = undefined;
    stopPrevious?.();

    currentContentId = data.contentId;

    try {
      recycleModules(modules, { reason: 'bridge-database-identity-changed' });
    } finally {
      unsubscribeViewIdentity = subscribeActiveViewIdentityChanges(
        currentContentId,
        onViewIdentityChanged,
        readViewIdentity,
      );
    }
  };

  eventBus.on('bridgeDatabaseIdentityChanged', onBridgeIdentityChanged);

  let disposed = false;

  return () => {
    if (disposed) {
      return;
    }

    disposed = true;
    eventBus.remove('bridgeDatabaseIdentityChanged', onBridgeIdentityChanged);

    const stopObservation = unsubscribeViewIdentity;
    unsubscribeViewIdentity = undefined;
    stopObservation?.();

    disposeModules(modules);
  };
}
